import React, { useState, useEffect, useMemo } from "react";
import FormGroup from "../../components/form-group";
import SelectMenu from "../../components/selectMenu";
import TaxesPaymentsService from "../../app/service/taxesPaymentsService";
import { messageError } from "../../components/toastr";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { pdfHeader } from "../../components/pdfHeaderPdfMake.js";
import customVfs from "../../components/customVfs";
import { Table, Button, Tabs, Tab } from "react-bootstrap";

// Configurações PDFMake
pdfMake.vfs = {
    ...pdfFonts.pdfMake.vfs,
    ...customVfs,
};

pdfMake.fonts = {
    Roboto: {
        normal: "Roboto-Regular.ttf",
        bold: "Roboto-Medium.ttf",
        italics: "Roboto-Italic.ttf",
        bolditalics: "Roboto-MediumItalic.ttf",
    },
    GreatVibes: {
        normal: "GreatVibes-Regular.ttf",
        bold: "GreatVibes-Regular.ttf",
        italics: "GreatVibes-Regular.ttf",
        bolditalics: "GreatVibes-Regular.ttf",
    },
};

// Função de formatação para moedas em Real
const formatBRL = (value) =>
    Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(value);

/**
 * Funções de agrupamento
 */
function groupByStatus(items) {
    const today = new Date();

    const pagos = items.filter((p) => p.dtPayment != null);
    const pendentes = items.filter((p) => {
        const expireDate = p.dtExpire ? new Date(p.dtExpire) : null;
        return p.dtPayment == null && expireDate && expireDate >= today;
    });
    const vencidos = items.filter((p) => {
        const expireDate = p.dtExpire ? new Date(p.dtExpire) : null;
        return p.dtPayment == null && expireDate && expireDate < today;
    });

    const result = [];
    if (pagos.length > 0) {
        result.push({ status: "Pago", items: pagos });
    }
    if (pendentes.length > 0) {
        result.push({ status: "Pendente", items: pendentes });
    }
    if (vencidos.length > 0) {
        result.push({ status: "Vencido", items: vencidos });
    }
    return result;
}

function groupByMemberAndReceipt(paymentsArray, members) {
    const mapMembers = new Map();
    for (const p of paymentsArray) {
        if (!mapMembers.has(p.member)) {
            mapMembers.set(p.member, []);
        }
        mapMembers.get(p.member).push(p);
    }

    const memberGroups = [];
    for (const [memberId, pays] of mapMembers.entries()) {
        memberGroups.push({ memberId, payments: pays });
    }

    memberGroups.sort((a, b) => {
        const nameA = (members.find((m) => m.id === a.memberId) || {}).name || "";
        const nameB = (members.find((m) => m.id === b.memberId) || {}).name || "";
        return nameA.localeCompare(nameB);
    });

    const result = [];
    for (const mg of memberGroups) {
        const receiptMap = new Map();
        const noReceiptList = [];

        mg.payments.forEach((p) => {
            const r = p.receipt;
            if (!r || r === "" || r === 0) {
                noReceiptList.push(p);
            } else {
                if (!receiptMap.has(r)) {
                    receiptMap.set(r, []);
                }
                receiptMap.get(r).push(p);
            }
        });

        const subGroups = [];

        for (const [receiptVal, arr] of receiptMap.entries()) {
            const splittedByStatus = groupByStatus(arr);
            splittedByStatus.forEach((groupStatus) => {
                subGroups.push({
                    receipt: receiptVal,
                    items: groupStatus.items,
                    status: groupStatus.status,
                });
            });
        }

        const splittedNoReceipt = groupByStatus(noReceiptList);
        splittedNoReceipt.forEach((groupStatus) => {
            subGroups.push({
                receipt: "",
                items: groupStatus.items,
                status: groupStatus.status,
            });
        });

        function statusOrder(s) {
            if (s === "Pago") return 1;
            if (s === "Pendente") return 2;
            if (s === "Vencido") return 3;
            return 4;
        }

        subGroups.sort((a, b) => statusOrder(a.status) - statusOrder(b.status));

        result.push({
            memberId: mg.memberId,
            subGroups,
        });
    }

    return result;
}

/**
 * Função de geração de PDF para a aba "Por Irmãos"
 */
function generatePdfReport({
    payments,
    members,
    selectedMember,
    selectedStatus,
    totalYearAll,
    totalYearPaid,
    totalYearOpen,
    totalYearVencido,
}) {
    const { headerContent, headerStyles } = pdfHeader("");

    const tableHeader = [
        { text: "Membro", bold: true, fillColor: "#eeeeee" },
        { text: "Descrição", bold: true, fillColor: "#eeeeee" },
        { text: "Vencimento", bold: true, fillColor: "#eeeeee" },
        { text: "Recibo", bold: true, fillColor: "#eeeeee" },
        { text: "Pagamento", bold: true, fillColor: "#eeeeee" },
        { text: "Valor", bold: true, fillColor: "#eeeeee", alignment: "right" },
        { text: "Total", bold: true, fillColor: "#eeeeee", alignment: "right" },
    ];
    const tableBody = [tableHeader];

    const grouped = groupByMemberAndReceipt(payments, members);

    for (const g of grouped) {
        const memberObj = members.find((m) => m.id === g.memberId);
        const memberName = memberObj ? memberObj.name : "";

        let totalLines = 0;
        g.subGroups.forEach((sg) => {
            totalLines += sg.items.length;
        });

        let currentLine = 0;

        for (const sg of g.subGroups) {
            const linesInThisReceipt = sg.items.length;

            let commonDtPaymentStr = "";
            if (sg.items[0].dtPayment) {
                const dtP = new Date(sg.items[0].dtPayment);
                commonDtPaymentStr = dtP.toLocaleDateString("pt-BR");
            }

            const subgroupTotal = sg.items.reduce(
                (acc, item) => acc + (item.amount || 0),
                0
            );

            let bgColorSubgroup = "#FFFFFF";
            const firstItem = sg.items[0];
            if (firstItem.dtPayment) {
                bgColorSubgroup = "#CEFFBA";
            } else if (
                firstItem.dtExpire &&
                new Date(firstItem.dtExpire) < new Date()
            ) {
                bgColorSubgroup = "#FFD1D1";
            }

            sg.items.forEach((p, index) => {
                const description = p.description
                    ? `${p.tax.taxGroup} - ${p.description}`
                    : `${p.tax.taxGroup} - ${p.tax.description}`;

                const dtExpireStr = p.dtExpire
                    ? new Date(p.dtExpire).toLocaleDateString("pt-BR")
                    : "";

                const amount = p.amount ? formatBRL(p.amount) : "";

                let bgColor = "#FFFFFF";
                if (p.dtPayment) {
                    bgColor = "#CEFFBA";
                } else if (p.dtExpire && new Date(p.dtExpire) < new Date()) {
                    bgColor = "#FFD1D1";
                }

                const row = [];

                if (currentLine === 0) {
                    row.push({
                        text: memberName,
                        rowSpan: totalLines,
                        bold: true,
                        alignment: "center",
                        fillColor: "#FFFFFF",
                    });
                } else {
                    row.push("");
                }

                row.push({ text: description, fillColor: bgColor });
                row.push({ text: dtExpireStr, fillColor: bgColor });

                if (sg.receipt && sg.receipt !== "" && index === 0) {
                    row.push({
                        text: String(sg.receipt),
                        rowSpan: linesInThisReceipt,
                        fillColor: bgColor,
                        alignment: "center",
                    });
                } else if (sg.receipt && sg.receipt !== "" && index > 0) {
                    row.push("");
                } else {
                    row.push({ text: "", fillColor: bgColor });
                }

                if (commonDtPaymentStr && index === 0) {
                    row.push({
                        text: commonDtPaymentStr,
                        rowSpan: linesInThisReceipt,
                        fillColor: bgColor,
                        alignment: "center",
                    });
                } else if (commonDtPaymentStr && index > 0) {
                    row.push("");
                } else {
                    const dtPaymentStr = p.dtPayment
                        ? new Date(p.dtPayment).toLocaleDateString("pt-BR")
                        : "";
                    row.push({
                        text: dtPaymentStr,
                        fillColor: bgColor,
                        alignment: "center",
                    });
                }

                row.push({ text: amount, fillColor: bgColor, alignment: "right" });

                if (index === 0) {
                    row.push({
                        text: formatBRL(subgroupTotal),
                        rowSpan: linesInThisReceipt,
                        fillColor: bgColorSubgroup,
                        alignment: "right",
                        bold: true,
                    });
                } else {
                    row.push("");
                }

                tableBody.push(row);
                currentLine++;
            });
        }
    }

    const selectedMemberName =
        selectedMember === "0" || selectedMember === ""
            ? "Todos os Membros"
            : `Membro: ${
                  members.find((m) => m.id === parseInt(selectedMember, 10))?.name || ""
              }`;

    let statusText = "Todos";
    if (selectedStatus === "Pagos") statusText = "Pagos";
    else if (selectedStatus === "Pendentes") statusText = "Pendentes";
    else if (selectedStatus === "Vencidos") statusText = "Vencidos";

    const documentDefinition = {
        content: [
            ...headerContent,
            { text: "Relatório de Pagamentos", style: "header" },
            { text: `${selectedMemberName} - ${statusText}`, style: "subheader" },
            {
                table: {
                    headerRows: 1,
                    widths: ["auto", "*", "auto", "auto", "auto", "auto", "auto"],
                    body: tableBody,
                },
            },
            {
                table: {
                    widths: ["*", "auto"],
                    body: [
                        [
                            { text: "Descrição", style: "tableHeader" },
                            { text: "Valor", style: "tableHeader", alignment: "right" },
                        ],
                        [
                            {
                                text: "Total Geral",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalYearAll),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                        [
                            {
                                text: "Total Recebido",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalYearPaid),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                        [
                            {
                                text: "Total em Aberto",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalYearOpen),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                        [
                            {
                                text: "Total Vencido",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalYearVencido),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                    ],
                },
                layout: {
                    fillColor: function () {
                        return null;
                    },
                },
                margin: [0, 20, 0, 0],
            },
        ],
        styles: {
            ...headerStyles,
            header: {
                fontSize: 18,
                bold: true,
                alignment: "center",
                margin: [0, 0, 0, 10],
            },
            subheader: {
                fontSize: 14,
                alignment: "center",
                margin: [0, 0, 0, 10],
            },
            tableHeader: {
                bold: true,
                fillColor: "#eeeeee",
                alignment: "center",
            },
        },
        footer: (currentPage, pageCount) => ({
            text: `${currentPage}/${pageCount}`,
            alignment: "right",
            margin: [0, 0, 40, 0],
        }),
    };

    pdfMake.createPdf(documentDefinition).open();
}

/**
 * Função de geração de PDF para a aba "Prestação de Contas"
 */
function generatePdfReportPrestacaoContas({
    groupedPayments,
    taxGroups,
    paymentTypes,
    totalReceived,
    totalDeposit,
    totalCash,
}) {
    const { headerContent, headerStyles } = pdfHeader("");

    const tableHeader = [
        { text: "Recibo", bold: true, fillColor: "#eeeeee" },
        { text: "Data do Pagamento", bold: true, fillColor: "#eeeeee" },
        { text: "Membro", bold: true, fillColor: "#eeeeee" },
        ...taxGroups.map((tg) => ({
            text: tg,
            bold: true,
            fillColor: "#eeeeee",
            alignment: "right",
        })),
        ...paymentTypes.map((pt) => ({
            text: pt,
            bold: true,
            fillColor: "#eeeeee",
            alignment: "right",
        })),
    ];

    const tableBody = [tableHeader];

    groupedPayments.forEach((group) => {
        const row = [
            { text: group.receipt, alignment: "center" },
            { text: group.paymentDate, alignment: "center" },
            { text: group.memberName, alignment: "center" },
        ];

        taxGroups.forEach((tg) => {
            row.push({
                text: group.taxGroups[tg] ? formatBRL(group.taxGroups[tg]) : "",
                alignment: "right",
            });
        });

        paymentTypes.forEach((pt) => {
            row.push({
                text: group.paymentTypes[pt] ? formatBRL(group.paymentTypes[pt]) : "",
                alignment: "right",
            });
        });

        tableBody.push(row);
    });

    const totalRow = [
        { text: "Total Geral", alignment: "center", bold: true },
        { text: "--", alignment: "center", bold: true },
        { text: "--", alignment: "center", bold: true },
        ...taxGroups.map((tg) => {
            const total = groupedPayments
                .filter((g) => g.taxGroups[tg])
                .reduce((acc, g) => acc + g.taxGroups[tg], 0);
            return {
                text: total > 0 ? formatBRL(total) : "",
                alignment: "right",
                bold: true,
            };
        }),
        ...paymentTypes.map((pt) => {
            const total = groupedPayments
                .filter((g) => g.paymentTypes[pt])
                .reduce((acc, g) => acc + g.paymentTypes[pt], 0);
            return {
                text: total > 0 ? formatBRL(total) : "",
                alignment: "right",
                bold: true,
            };
        }),
    ];

    tableBody.push(totalRow);

    const documentDefinition = {
        content: [
            ...headerContent,
            { text: "Prestação de Contas", style: "header" },
            {
                table: {
                    headerRows: 1,
                    widths: [
                        "auto",
                        "auto",
                        "*",
                        ...taxGroups.map(() => "auto"),
                        ...paymentTypes.map(() => "auto"),
                    ],
                    body: tableBody,
                },
            },
            {
                table: {
                    widths: ["*", "auto"],
                    body: [
                        [
                            { text: "Descrição", style: "tableHeader" },
                            { text: "Valor", style: "tableHeader", alignment: "right" },
                        ],
                        [
                            {
                                text: "Total Geral",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalReceived),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                        [
                            {
                                text: "Total Geral em Depósito",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalDeposit),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                        [
                            {
                                text: "Total Geral em Espécie",
                                bold: true,
                                alignment: "left",
                            },
                            {
                                text: formatBRL(totalCash),
                                alignment: "right",
                                bold: true,
                            },
                        ],
                    ],
                },
                layout: {
                    fillColor: function () {
                        return null;
                    },
                },
                margin: [0, 20, 0, 0],
            },
        ],
        styles: {
            ...headerStyles,
            header: {
                fontSize: 18,
                bold: true,
                alignment: "center",
                margin: [0, 0, 0, 10],
            },
            subheader: {
                fontSize: 14,
                alignment: "center",
                margin: [0, 0, 0, 10],
            },
            tableHeader: {
                bold: true,
                fillColor: "#eeeeee",
                alignment: "center",
            },
        },
        footer: (currentPage, pageCount) => ({
            text: `${currentPage}/${pageCount}`,
            alignment: "right",
            margin: [0, 0, 40, 0],
        }),
    };

    pdfMake.createPdf(documentDefinition).open();
}

const ReportPayments = ({ members }) => {
    const [allPayments, setAllPayments] = useState([]);

    // Aba "Por Irmãos"
    const [selectedMemberPorIrmaos, setSelectedMemberPorIrmaos] = useState("");
    const [selectedStatusPorIrmaos, setSelectedStatusPorIrmaos] = useState("");
    const [selectedYearPorIrmaos, setSelectedYearPorIrmaos] = useState("");

    // Aba "Anual"
    const [selectedMemberLoja, setSelectedMemberLoja] = useState("");
    const [selectedStatusLoja, setSelectedStatusLoja] = useState("");
    const [selectedYearLoja, setSelectedYearLoja] = useState("");

    // Aba "Prestação de Contas"
    const [taxGroups, setTaxGroups] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [selectedYearPrestacaoContas, setSelectedYearPrestacaoContas] = useState("");

    const taxesPaymentsService = useMemo(() => new TaxesPaymentsService(), []);

    useEffect(() => {
        loadAllPayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadTaxGroups();
        loadPaymentTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPayments]);

    const loadAllPayments = async () => {
        try {
            const response = await taxesPaymentsService.getAll();
            setAllPayments(response.data || []);
        } catch (error) {
            console.error("Erro ao carregar pagamentos:", error);
            messageError("Erro ao carregar pagamentos.");
        }
    };

    const loadTaxGroups = () => {
        const groups = new Set();
        allPayments.forEach((p) => {
            if (p.tax && p.tax.taxGroup) {
                groups.add(p.tax.taxGroup);
            }
        });
        setTaxGroups(Array.from(groups));
    };

    const loadPaymentTypes = () => {
        const types = new Set();
        allPayments.forEach((p) => {
            if (p.paymentType) {
                types.add(p.paymentType);
            }
        });
        setPaymentTypes(Array.from(types));
    };

    const availableYears = useMemo(() => {
        const years = new Set();

        allPayments.forEach((p) => {
            if (p.dtPayment) {
                years.add(String(new Date(p.dtPayment).getFullYear()));
            }
        });

        return Array.from(years).sort((a, b) => Number(b) - Number(a));
    }, [allPayments]);

    // Filtro para a aba "Por Irmãos"
    const paymentsPorIrmaos = useMemo(() => {
        if (!allPayments) return [];

        let data = [...allPayments];

        // Filtro por ano considerando apenas a data do pagamento
        if (selectedYearPorIrmaos !== "") {
            data = data.filter((p) => {
                if (!p.dtPayment) return false;
                return String(new Date(p.dtPayment).getFullYear()) === selectedYearPorIrmaos;
            });
        }

        // Filtro por membro
        if (selectedMemberPorIrmaos !== "" && selectedMemberPorIrmaos !== "0") {
            data = data.filter((p) => p.member === parseInt(selectedMemberPorIrmaos, 10));
        }

        // Filtro por status
        const today = new Date();
        if (selectedStatusPorIrmaos === "Pagos") {
            data = data.filter((p) => p.dtPayment != null);
        } else if (selectedStatusPorIrmaos === "Pendentes") {
            data = data.filter((p) => p.dtPayment == null);
        } else if (selectedStatusPorIrmaos === "Vencidos") {
            data = data.filter((p) => {
                const expireDate = p.dtExpire ? new Date(p.dtExpire) : null;
                return p.dtPayment == null && expireDate && expireDate < today;
            });
        }

        return data;
    }, [
        allPayments,
        selectedMemberPorIrmaos,
        selectedStatusPorIrmaos,
        selectedYearPorIrmaos,
    ]);

    // Filtro para a aba "Anual"
    const paymentsLoja = useMemo(() => {
        if (!allPayments) return [];

        let data = [...allPayments];

        // Filtro por ano considerando apenas a data do pagamento
        if (selectedYearLoja !== "") {
            data = data.filter((p) => {
                if (!p.dtPayment) return false;
                return String(new Date(p.dtPayment).getFullYear()) === selectedYearLoja;
            });
        }

        // Filtro por membro na Loja
        if (selectedMemberLoja !== "" && selectedMemberLoja !== "0") {
            data = data.filter((p) => p.member === parseInt(selectedMemberLoja, 10));
        }

        // Filtro por status na Loja
        const today = new Date();
        if (selectedStatusLoja === "Pagos") {
            data = data.filter((p) => p.dtPayment != null);
        } else if (selectedStatusLoja === "Pendentes") {
            data = data.filter((p) => {
                const expireDate = p.dtExpire ? new Date(p.dtExpire) : null;
                return p.dtPayment == null && expireDate && expireDate >= today;
            });
        } else if (selectedStatusLoja === "Vencidos") {
            data = data.filter((p) => {
                const expireDate = p.dtExpire ? new Date(p.dtExpire) : null;
                return p.dtPayment == null && expireDate && expireDate < today;
            });
        }

        return data;
    }, [allPayments, selectedMemberLoja, selectedStatusLoja, selectedYearLoja]);

    // Filtro para a aba "Prestação de Contas"
    const paymentsPrestacaoContas = useMemo(() => {
        if (!allPayments) return [];

        let data = allPayments.filter((p) => p.dtPayment != null);

        // Filtro por ano considerando apenas a data do pagamento
        if (selectedYearPrestacaoContas !== "") {
            data = data.filter(
                (p) =>
                    String(new Date(p.dtPayment).getFullYear()) ===
                    selectedYearPrestacaoContas
            );
        }

        data.sort((a, b) => {
            if (a.receipt === "" || a.receipt === null) return 1;
            if (b.receipt === "" || b.receipt === null) return -1;

            if (typeof a.receipt === "number" && typeof b.receipt === "number") {
                return a.receipt - b.receipt;
            }

            return String(a.receipt).localeCompare(String(b.receipt));
        });

        return data;
    }, [allPayments, selectedYearPrestacaoContas]);

    const [
        monthlyTotalsPorIrmaos,
        monthlyTotalsPaidPorIrmaos,
        monthlyTotalsOpenPorIrmaos,
        monthlyTotalsVencidosPorIrmaos,
    ] = useMemo(() => {
        const totals = Array(12).fill(0);
        const totalsPaid = Array(12).fill(0);
        const totalsOpen = Array(12).fill(0);
        const totalsVencidos = Array(12).fill(0);

        paymentsPorIrmaos.forEach((p) => {
            if (!p.dtExpire || !p.amount) return;
            const m = new Date(p.dtExpire).getMonth();
            totals[m] += p.amount;

            if (p.dtPayment) {
                totalsPaid[m] += p.amount;
            } else {
                totalsOpen[m] += p.amount;
                const today = new Date();
                if (new Date(p.dtExpire) < today) {
                    totalsVencidos[m] += p.amount;
                }
            }
        });

        return [totals, totalsPaid, totalsOpen, totalsVencidos];
    }, [paymentsPorIrmaos]);

    const sumArray = (arr) => arr.reduce((acc, val) => acc + val, 0);

    const totalYearAllPorIrmaos = sumArray(monthlyTotalsPorIrmaos);
    const totalYearPaidPorIrmaos = sumArray(monthlyTotalsPaidPorIrmaos);
    const totalYearOpenPorIrmaos = sumArray(monthlyTotalsOpenPorIrmaos);
    const totalYearVencidoPorIrmaos = sumArray(monthlyTotalsVencidosPorIrmaos);

    const [
        monthlyTotalsLoja,
        monthlyTotalsPaidLoja,
        monthlyTotalsOpenLoja,
        monthlyTotalsVencidosLoja,
        monthlyTotalsMembersLoja,
        monthlyTotalsVencidosMembersLoja,
        monthlyTotalsOthersLoja,
        monthlyTotalsVencidosOthersLoja,
        monthlyTotalsPaidMembers,
        monthlyTotalsPaidOthers,
        monthlyTotalsOpenMembers,
        monthlyTotalsOpenOthers,
    ] = useMemo(() => {
        const totals = Array(12).fill(0);
        const totalsPaid = Array(12).fill(0);
        const totalsOpen = Array(12).fill(0);
        const totalsOpenMembers = Array(12).fill(0);
        const totalsOpenOthers = Array(12).fill(0);
        const totalsMembers = Array(12).fill(0);
        const totalsOthers = Array(12).fill(0);
        const totalsPaidMembers = Array(12).fill(0);
        const totalsPaidOthers = Array(12).fill(0);
        const totalsVencidos = Array(12).fill(0);
        const totalsVencidosMembers = Array(12).fill(0);
        const totalsVencidosOthers = Array(12).fill(0);

        paymentsLoja.forEach((p) => {
            if (!p.dtExpire || !p.amount) return;
            const m = new Date(p.dtExpire).getMonth();
            totals[m] += p.amount;

            if (p.dtPayment) {
                totalsPaid[m] += p.amount;
                if (p.member == null || p.member === "") {
                    totalsPaidOthers[m] += p.amount;
                } else {
                    totalsPaidMembers[m] += p.amount;
                }
            } else {
                totalsOpen[m] += p.amount;
                const today = new Date();
                if (new Date(p.dtExpire) < today) {
                    totalsVencidos[m] += p.amount;
                }
            }

            if (p.member == null || p.member === "") {
                totalsOthers[m] += p.amount;
                const today = new Date();
                if (new Date(p.dtExpire) < today) {
                    totalsVencidosOthers[m] += p.amount;
                } else {
                    totalsOpenOthers[m] += p.amount;
                }
            } else {
                totalsMembers[m] += p.amount;
                const today = new Date();
                if (new Date(p.dtExpire) < today) {
                    totalsVencidosMembers[m] += p.amount;
                } else {
                    totalsOpenMembers[m] += p.amount;
                }
            }
        });

        return [
            totals,
            totalsPaid,
            totalsOpen,
            totalsVencidos,
            totalsMembers,
            totalsVencidosMembers,
            totalsOthers,
            totalsVencidosOthers,
            totalsPaidMembers,
            totalsPaidOthers,
            totalsOpenMembers,
            totalsOpenOthers,
        ];
    }, [paymentsLoja]);

    const totalYearAllLoja = sumArray(monthlyTotalsLoja);
    const totalYearPaidLoja = sumArray(monthlyTotalsPaidLoja);
    const totalYearOpenLoja = sumArray(monthlyTotalsOpenLoja);
    const totalYearVencidoLoja = sumArray(monthlyTotalsVencidosLoja);
    const totalYearMembersLoja = sumArray(monthlyTotalsMembersLoja);
    const totalYearVencidoMembersLoja = sumArray(monthlyTotalsVencidosMembersLoja);
    const totalYearOthersLoja = sumArray(monthlyTotalsOthersLoja);
    const totalYearVencidoOthersLoja = sumArray(monthlyTotalsVencidosOthersLoja);
    const totalYearPaidMembers = sumArray(monthlyTotalsPaidMembers);
    const totalYearPaidOthers = sumArray(monthlyTotalsPaidOthers);
    const totalYearOpenMembers = sumArray(monthlyTotalsOpenMembers);
    const totalYearOpenOthers = sumArray(monthlyTotalsOpenOthers);

    const groupedPaymentsPrestacaoContas = useMemo(() => {
        const groups = {};

        const sortedPayments = [...paymentsPrestacaoContas].sort((a, b) => {
            if (a.receipt === "" || a.receipt === null) return 1;
            if (b.receipt === "" || b.receipt === null) return -1;
            if (typeof a.receipt === "number" && typeof b.receipt === "number") {
                return a.receipt - b.receipt;
            }
            return String(a.receipt).localeCompare(String(b.receipt));
        });

        sortedPayments.forEach((p) => {
            const receipt = p.receipt || "";
            if (!groups[receipt]) {
                const member = members.find((m) => m.id === p.member);
                groups[receipt] = {
                    receipt,
                    paymentDate: p.dtPayment
                        ? new Date(p.dtPayment).toLocaleDateString("pt-BR")
                        : "",
                    memberName:
                        member && p.description
                            ? `${member.name} - ${p.description}`
                            : member
                            ? member.name
                            : p.description,
                    taxGroups: {},
                    paymentTypes: {},
                    totalAmount: 0,
                };
            }

            const group = groups[receipt];

            if (p.tax && p.tax.taxGroup) {
                group.taxGroups[p.tax.taxGroup] =
                    (group.taxGroups[p.tax.taxGroup] || 0) + (p.amount || 0);
            }

            if (p.paymentType) {
                group.paymentTypes[p.paymentType] =
                    (group.paymentTypes[p.paymentType] || 0) + (p.amount || 0);
            }

            group.totalAmount += p.amount || 0;
        });

        return Object.values(groups);
    }, [paymentsPrestacaoContas, members]);

    const totalReceivedPrestacaoContas = useMemo(() => {
        return paymentsPrestacaoContas.reduce((acc, p) => acc + (p.amount || 0), 0);
    }, [paymentsPrestacaoContas]);

    const totalDepositPrestacaoContas = useMemo(() => {
        return groupedPaymentsPrestacaoContas
            .filter((g) => g.paymentTypes["Depósito"])
            .reduce((acc, g) => acc + g.paymentTypes["Depósito"], 0);
    }, [groupedPaymentsPrestacaoContas]);

    const totalCashPrestacaoContas = useMemo(() => {
        return groupedPaymentsPrestacaoContas
            .filter((g) => g.paymentTypes["Em espécie"])
            .reduce((acc, g) => acc + g.paymentTypes["Em espécie"], 0);
    }, [groupedPaymentsPrestacaoContas]);

    const availableTaxGroups = useMemo(() => {
        const groups = new Set();
        allPayments.forEach((p) => {
            if (p.tax && p.tax.taxGroup) {
                groups.add(p.tax.taxGroup);
            }
        });
        return Array.from(groups);
    }, [allPayments]);

    const availablePaymentTypes = useMemo(() => {
        const types = new Set();
        allPayments.forEach((p) => {
            if (p.paymentType) {
                types.add(p.paymentType);
            }
        });
        return Array.from(types);
    }, [allPayments]);

    const handleGeneratePdfPorIrmaos = () => {
        generatePdfReport({
            payments: paymentsPorIrmaos,
            members,
            selectedMember: selectedMemberPorIrmaos,
            selectedStatus: selectedStatusPorIrmaos,
            totalYearAll: totalYearAllPorIrmaos,
            totalYearPaid: totalYearPaidPorIrmaos,
            totalYearOpen: totalYearOpenPorIrmaos,
            totalYearVencido: totalYearVencidoPorIrmaos,
        });
    };

    const handleGeneratePdfPrestacaoContas = () => {
        generatePdfReportPrestacaoContas({
            groupedPayments: groupedPaymentsPrestacaoContas,
            taxGroups: availableTaxGroups,
            paymentTypes: availablePaymentTypes,
            totalReceived: totalReceivedPrestacaoContas,
            totalDeposit: totalDepositPrestacaoContas,
            totalCash: totalCashPrestacaoContas,
        });
    };

    const renderRows = (grouped) => {
        const rows = [];

        for (const g of grouped) {
            const memberObj = members.find((m) => m.id === g.memberId);
            const memberName = memberObj ? memberObj.name : "Outros";

            let totalLines = 0;
            g.subGroups.forEach((sg) => {
                totalLines += sg.items.length;
            });

            let currentLine = 0;

            for (const sg of g.subGroups) {
                const linesInThisReceipt = sg.items.length;

                let commonDtPaymentStr = "";
                if (sg.items[0].dtPayment) {
                    const dtP = new Date(sg.items[0].dtPayment);
                    commonDtPaymentStr = dtP.toLocaleDateString("pt-BR");
                }

                const subgroupTotal = sg.items.reduce(
                    (acc, item) => acc + (item.amount || 0),
                    0
                );

                let bgColorSubgroup = "#FFFFFF";
                const firstItem = sg.items[0];
                if (firstItem.dtPayment) {
                    bgColorSubgroup = "#CEFFBA";
                } else if (
                    firstItem.dtExpire &&
                    new Date(firstItem.dtExpire) < new Date()
                ) {
                    bgColorSubgroup = "#FFD1D1";
                }

                sg.items.forEach((p, index) => {
                    const description = p.description
                        ? `${p.tax.taxGroup} - ${p.description}`
                        : `${p.tax.taxGroup} - ${p.tax.description}`;

                    const dtExpireStr = p.dtExpire
                        ? new Date(p.dtExpire).toLocaleDateString("pt-BR")
                        : "";

                    const amount = p.amount ? formatBRL(p.amount) : "";

                    let bgColor = "#FFFFFF";
                    if (p.dtPayment) {
                        bgColor = "#CEFFBA";
                    } else if (p.dtExpire && new Date(p.dtExpire) < new Date()) {
                        bgColor = "#FFD1D1";
                    }

                    const row = [];

                    if (currentLine === 0) {
                        row.push({
                            text: memberName,
                            rowSpan: totalLines,
                            bold: true,
                            alignment: "center",
                            fillColor: "#FFFFFF",
                        });
                    } else {
                        row.push("");
                    }

                    row.push({ text: description, fillColor: bgColor });
                    row.push({ text: dtExpireStr, fillColor: bgColor });

                    if (sg.receipt && sg.receipt !== "" && index === 0) {
                        row.push({
                            text: String(sg.receipt),
                            rowSpan: linesInThisReceipt,
                            fillColor: bgColor,
                            alignment: "center",
                        });
                    } else if (sg.receipt && sg.receipt !== "" && index > 0) {
                        row.push("");
                    } else {
                        row.push({ text: "", fillColor: bgColor });
                    }

                    if (commonDtPaymentStr && index === 0) {
                        row.push({
                            text: commonDtPaymentStr,
                            rowSpan: linesInThisReceipt,
                            fillColor: bgColor,
                            alignment: "center",
                        });
                    } else if (commonDtPaymentStr && index > 0) {
                        row.push("");
                    } else {
                        const dtPaymentStr = p.dtPayment
                            ? new Date(p.dtPayment).toLocaleDateString("pt-BR")
                            : "";
                        row.push({
                            text: dtPaymentStr,
                            fillColor: bgColor,
                            alignment: "center",
                        });
                    }

                    row.push({ text: amount, fillColor: bgColor, alignment: "right" });

                    if (index === 0) {
                        row.push({
                            text: formatBRL(subgroupTotal),
                            rowSpan: linesInThisReceipt,
                            fillColor: bgColorSubgroup,
                            alignment: "right",
                            bold: true,
                        });
                    } else {
                        row.push("");
                    }

                    rows.push(row);
                    currentLine++;
                });
            }
        }

        return rows;
    };

    return (
        <div style={{ margin: "2rem" }}>
            <Tabs defaultActiveKey="porIrmaos" id="report-payments-tabs">
                {/* ABA "Por Irmãos" */}
                <Tab eventKey="porIrmaos" title="Por Irmãos">
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "flex-end",
                            marginBottom: "20px",
                            flexWrap: "wrap",
                        }}
                    >
                        <FormGroup label="Irmão:" htmlFor="selectMemberPorIrmaos">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Selecione", value: "" },
                                    { label: "Todos", value: "0" },
                                    ...members.map((member) => ({
                                        label: member.name,
                                        value: member.id,
                                    })),
                                ]}
                                value={selectedMemberPorIrmaos}
                                onChange={(e) => setSelectedMemberPorIrmaos(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup label="Situação:" htmlFor="selectStatusPorIrmaos">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    { label: "Pagos", value: "Pagos" },
                                    { label: "Pendentes", value: "Pendentes" },
                                    { label: "Vencidos", value: "Vencidos" },
                                ]}
                                value={selectedStatusPorIrmaos}
                                onChange={(e) => setSelectedStatusPorIrmaos(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup label="Ano de pagamento:" htmlFor="selectYearPorIrmaos">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    ...availableYears.map((year) => ({
                                        label: year,
                                        value: year,
                                    })),
                                ]}
                                value={selectedYearPorIrmaos}
                                onChange={(e) => setSelectedYearPorIrmaos(e.target.value)}
                            />
                        </FormGroup>
                    </div>

                    {paymentsPorIrmaos.length > 0 ? (
                        <>
                            <Table
                                bordered
                                size="sm"
                                style={{
                                    marginTop: "1rem",
                                    border: "3px solid black",
                                    borderCollapse: "collapse",
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>Membro / Outros</th>
                                        <th style={{ textAlign: "center" }}>Descrição</th>
                                        <th style={{ textAlign: "center" }}>Vencimento</th>
                                        <th style={{ textAlign: "center" }}>Recibo</th>
                                        <th style={{ textAlign: "center" }}>Pagamento</th>
                                        <th style={{ textAlign: "center" }}>Valor</th>
                                        <th style={{ textAlign: "center" }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const grouped = groupByMemberAndReceipt(paymentsPorIrmaos, members);
                                        const rows = renderRows(grouped);

                                        return rows.map((row, index) => (
                                            <tr key={index}>
                                                {row.map((cell, cellIndex) => {
                                                    if (cell === "") {
                                                        return (
                                                            <td
                                                                key={cellIndex}
                                                                style={{ display: "none" }}
                                                            />
                                                        );
                                                    }

                                                    return (
                                                        <td
                                                            key={cellIndex}
                                                            rowSpan={cell.rowSpan || 1}
                                                            style={{
                                                                textAlign: cell.alignment || "left",
                                                                backgroundColor:
                                                                    cell.fillColor || "transparent",
                                                                fontWeight: cell.bold
                                                                    ? "bold"
                                                                    : "normal",
                                                            }}
                                                        >
                                                            {cell.text}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </Table>

                            <Table
                                bordered
                                size="sm"
                                style={{
                                    marginTop: "1rem",
                                    border: "2px solid black",
                                    borderCollapse: "collapse",
                                    width: "30%",
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "left",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            Total Geral
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            {totalYearAllPorIrmaos > 0
                                                ? formatBRL(totalYearAllPorIrmaos)
                                                : "R$ 0,00"}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "left",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            Total Recebido
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            {totalYearPaidPorIrmaos > 0
                                                ? formatBRL(totalYearPaidPorIrmaos)
                                                : "R$ 0,00"}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "left",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            Total em Aberto
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                padding: "4px 8px",
                                            }}
                                        >
                                            {totalYearOpenPorIrmaos > 0
                                                ? formatBRL(totalYearOpenPorIrmaos)
                                                : "R$ 0,00"}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "left",
                                                padding: "4px 8px",
                                                color: "red",
                                            }}
                                        >
                                            Total Vencido
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                padding: "4px 8px",
                                                color: "red",
                                            }}
                                        >
                                            {totalYearVencidoPorIrmaos > 0
                                                ? formatBRL(totalYearVencidoPorIrmaos)
                                                : "R$ 0,00"}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            <Button
                                variant="info"
                                onClick={handleGeneratePdfPorIrmaos}
                                style={{ marginTop: "1rem" }}
                            >
                                Gerar PDF
                            </Button>
                        </>
                    ) : (
                        <p>Nenhum pagamento encontrado para os critérios selecionados.</p>
                    )}
                </Tab>

                {/* ABA "Anual" */}
                <Tab eventKey="anual" title="Anual">
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "flex-end",
                            marginBottom: "20px",
                            flexWrap: "wrap",
                        }}
                    >
                        <FormGroup label="Irmão:" htmlFor="selectMemberLoja">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Selecione", value: "" },
                                    { label: "Todos", value: "0" },
                                    ...members.map((member) => ({
                                        label: member.name,
                                        value: member.id,
                                    })),
                                ]}
                                value={selectedMemberLoja}
                                onChange={(e) => setSelectedMemberLoja(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup label="Situação:" htmlFor="selectStatusLoja">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    { label: "Pagos", value: "Pagos" },
                                    { label: "Pendentes", value: "Pendentes" },
                                    { label: "Vencidos", value: "Vencidos" },
                                ]}
                                value={selectedStatusLoja}
                                onChange={(e) => setSelectedStatusLoja(e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup label="Ano do pagamento:" htmlFor="selectYearLoja">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    ...availableYears.map((year) => ({
                                        label: year,
                                        value: year,
                                    })),
                                ]}
                                value={selectedYearLoja}
                                onChange={(e) => setSelectedYearLoja(e.target.value)}
                            />
                        </FormGroup>
                    </div>

                    {paymentsLoja.length === 0 ? (
                        <p>Nenhum pagamento encontrado para os critérios selecionados.</p>
                    ) : (
                        <>
                            <Table
                                striped
                                bordered
                                hover
                                size="sm"
                                style={{ border: "3px solid black", borderCollapse: "collapse" }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}> </th>
                                        {[...Array(12).keys()].map((i) => {
                                            const monthName = new Date(0, i)
                                                .toLocaleString("pt-BR", { month: "short" })
                                                .toUpperCase();
                                            return (
                                                <th key={i} style={{ textAlign: "center" }}>
                                                    {monthName}
                                                </th>
                                            );
                                        })}
                                        <th style={{ textAlign: "center" }}>TOTAL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                            }}
                                        >
                                            TOTAL GERAL
                                        </td>
                                        {monthlyTotalsLoja.map((val, i) => (
                                            <td
                                                key={i}
                                                style={{ textAlign: "right", fontWeight: "bold" }}
                                            >
                                                {val > 0 ? formatBRL(val) : ""}
                                            </td>
                                        ))}
                                        <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                            {totalYearAllLoja > 0
                                                ? formatBRL(totalYearAllLoja)
                                                : ""}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                color: "green",
                                            }}
                                        >
                                            RECEBIDOS TOTAL
                                        </td>
                                        {monthlyTotalsPaidLoja.map((val, i) => (
                                            <td
                                                key={`paid-${i}`}
                                                style={{
                                                    textAlign: "right",
                                                    fontWeight: "bold",
                                                    color: "green",
                                                }}
                                            >
                                                {val > 0 ? formatBRL(val) : ""}
                                            </td>
                                        ))}
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                color: "green",
                                            }}
                                        >
                                            {totalYearPaidLoja > 0
                                                ? formatBRL(totalYearPaidLoja)
                                                : ""}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                            }}
                                        >
                                            EM ABERTO TOTAL
                                        </td>
                                        {monthlyTotalsOpenLoja.map((val, i) => (
                                            <td
                                                key={`open-${i}`}
                                                style={{
                                                    textAlign: "right",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {val > 0 ? formatBRL(val) : ""}
                                            </td>
                                        ))}
                                        <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                            {totalYearOpenLoja > 0
                                                ? formatBRL(totalYearOpenLoja)
                                                : ""}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td
                                            style={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                                color: "red",
                                            }}
                                        >
                                            VENCIDOS TOTAL
                                        </td>
                                        {monthlyTotalsVencidosLoja.map((val, i) => (
                                            <td
                                                key={`vencido-${i}`}
                                                style={{
                                                    textAlign: "right",
                                                    fontWeight: "bold",
                                                    color: "red",
                                                }}
                                            >
                                                {val > 0 ? formatBRL(val) : ""}
                                            </td>
                                        ))}
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                                color: "red",
                                            }}
                                        >
                                            {totalYearVencidoLoja > 0
                                                ? formatBRL(totalYearVencidoLoja)
                                                : ""}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            {selectedMemberLoja === "0" && (
                                <>
                                    <Table
                                        striped
                                        bordered
                                        hover
                                        size="sm"
                                        style={{
                                            border: "3px solid black",
                                            borderCollapse: "collapse",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: "center" }}> </th>
                                                {[...Array(12).keys()].map((i) => {
                                                    const monthName = new Date(0, i)
                                                        .toLocaleString("pt-BR", {
                                                            month: "short",
                                                        })
                                                        .toUpperCase();
                                                    return (
                                                        <th key={i} style={{ textAlign: "center" }}>
                                                            {monthName}
                                                        </th>
                                                    );
                                                })}
                                                <th style={{ textAlign: "center" }}>TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "black",
                                                    }}
                                                >
                                                    TOTAL IRMÃOS
                                                </td>
                                                {monthlyTotalsMembersLoja.map((val, i) => (
                                                    <td
                                                        key={`members-total-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "black",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                    }}
                                                >
                                                    {totalYearMembersLoja > 0
                                                        ? formatBRL(totalYearMembersLoja)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "green",
                                                    }}
                                                >
                                                    RECEBIDOS IRMÃOS
                                                </td>
                                                {monthlyTotalsPaidMembers.map((val, i) => (
                                                    <td
                                                        key={`members-paid-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "green",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "green",
                                                    }}
                                                >
                                                    {totalYearPaidMembers > 0
                                                        ? formatBRL(totalYearPaidMembers)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "black",
                                                    }}
                                                >
                                                    EM ABERTO IRMÃOS
                                                </td>
                                                {monthlyTotalsOpenMembers.map((val, i) => (
                                                    <td
                                                        key={`members-open-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "black",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                    }}
                                                >
                                                    {totalYearOpenMembers > 0
                                                        ? formatBRL(totalYearOpenMembers)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "red",
                                                    }}
                                                >
                                                    VENCIDOS IRMÃOS
                                                </td>
                                                {monthlyTotalsVencidosMembersLoja.map((val, i) => (
                                                    <td
                                                        key={`members-vencidos-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "red",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "red",
                                                    }}
                                                >
                                                    {totalYearVencidoMembersLoja > 0
                                                        ? formatBRL(totalYearVencidoMembersLoja)
                                                        : ""}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>

                                    <Table
                                        striped
                                        bordered
                                        hover
                                        size="sm"
                                        style={{
                                            border: "3px solid black",
                                            borderCollapse: "collapse",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: "center" }}> </th>
                                                {[...Array(12).keys()].map((i) => {
                                                    const monthName = new Date(0, i)
                                                        .toLocaleString("pt-BR", {
                                                            month: "short",
                                                        })
                                                        .toUpperCase();
                                                    return (
                                                        <th key={i} style={{ textAlign: "center" }}>
                                                            {monthName}
                                                        </th>
                                                    );
                                                })}
                                                <th style={{ textAlign: "center" }}>TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "black",
                                                    }}
                                                >
                                                    TOTAL OUTROS
                                                </td>
                                                {monthlyTotalsOthersLoja.map((val, i) => (
                                                    <td
                                                        key={`others-total-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "black",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                    }}
                                                >
                                                    {totalYearOthersLoja > 0
                                                        ? formatBRL(totalYearOthersLoja)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "green",
                                                    }}
                                                >
                                                    RECEBIDOS OUTROS
                                                </td>
                                                {monthlyTotalsPaidOthers.map((val, i) => (
                                                    <td
                                                        key={`others-paid-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "green",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "green",
                                                    }}
                                                >
                                                    {totalYearPaidOthers > 0
                                                        ? formatBRL(totalYearPaidOthers)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "black",
                                                    }}
                                                >
                                                    EM ABERTO OUTROS
                                                </td>
                                                {monthlyTotalsOpenOthers.map((val, i) => (
                                                    <td
                                                        key={`others-open-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "black",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "black",
                                                    }}
                                                >
                                                    {totalYearOpenOthers > 0
                                                        ? formatBRL(totalYearOpenOthers)
                                                        : ""}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td
                                                    style={{
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        color: "red",
                                                    }}
                                                >
                                                    VENCIDOS OUTROS
                                                </td>
                                                {monthlyTotalsVencidosOthersLoja.map((val, i) => (
                                                    <td
                                                        key={`others-vencidos-${i}`}
                                                        style={{
                                                            textAlign: "right",
                                                            fontWeight: "bold",
                                                            color: "red",
                                                        }}
                                                    >
                                                        {val > 0 ? formatBRL(val) : ""}
                                                    </td>
                                                ))}
                                                <td
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                        color: "red",
                                                    }}
                                                >
                                                    {totalYearVencidoOthersLoja > 0
                                                        ? formatBRL(totalYearVencidoOthersLoja)
                                                        : ""}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </>
                            )}
                        </>
                    )}
                </Tab>

                {/* ABA "Prestação de Contas" */}
                <Tab eventKey="prestacaoContas" title="Prestação de Contas">
                    <div
                        style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "flex-end",
                            marginBottom: "20px",
                            flexWrap: "wrap",
                        }}
                    >
                        <FormGroup label="Fonte:" htmlFor="selectTaxGroupPrestacaoContas">
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    ...taxGroups.map((group) => ({
                                        label: group,
                                        value: group,
                                    })),
                                ]}
                                value={""}
                                onChange={() => {}}
                            />
                        </FormGroup>

                        <FormGroup
                            label="Ano de pagamento:"
                            htmlFor="selectYearPrestacaoContas"
                        >
                            <SelectMenu
                                className="form-control"
                                list={[
                                    { label: "Todos", value: "" },
                                    ...availableYears.map((year) => ({
                                        label: year,
                                        value: year,
                                    })),
                                ]}
                                value={selectedYearPrestacaoContas}
                                onChange={(e) =>
                                    setSelectedYearPrestacaoContas(e.target.value)
                                }
                            />
                        </FormGroup>
                    </div>

                    {groupedPaymentsPrestacaoContas.length === 0 ? (
                        <p>Nenhum pagamento recebido encontrado.</p>
                    ) : (
                        <>
                            <Table
                                striped
                                bordered
                                hover
                                size="sm"
                                style={{
                                    border: "3px solid black",
                                    borderCollapse: "collapse",
                                    width: "100%",
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>Recibo</th>
                                        <th style={{ textAlign: "center" }}>
                                            Data do Pagamento
                                        </th>
                                        <th style={{ textAlign: "center" }}>
                                            Irmão / Descrição
                                        </th>
                                        {availableTaxGroups.map((tg) => (
                                            <th
                                                key={`tax-${tg}`}
                                                style={{ textAlign: "center" }}
                                            >
                                                {tg}
                                            </th>
                                        ))}
                                        {availablePaymentTypes.map((pt) => (
                                            <th
                                                key={`pt-${pt}`}
                                                style={{ textAlign: "center" }}
                                            >
                                                {pt}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedPaymentsPrestacaoContas.map((group, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: "center" }}>
                                                {group.receipt}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {group.paymentDate}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {group.memberName}
                                            </td>

                                            {availableTaxGroups.map((tg, idx) => (
                                                <td
                                                    key={`tg-${idx}`}
                                                    style={{ textAlign: "right" }}
                                                >
                                                    {group.taxGroups[tg]
                                                        ? formatBRL(group.taxGroups[tg])
                                                        : ""}
                                                </td>
                                            ))}

                                            {availablePaymentTypes.map((pt, idx) => (
                                                <td
                                                    key={`pt-${idx}`}
                                                    style={{ textAlign: "right" }}
                                                >
                                                    {group.paymentTypes[pt]
                                                        ? formatBRL(group.paymentTypes[pt])
                                                        : ""}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    <tr>
                                        <td
                                            style={{
                                                textAlign: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Total Geral
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            --
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            --
                                        </td>

                                        {availableTaxGroups.map((tg, idx) => {
                                            const total = groupedPaymentsPrestacaoContas
                                                .filter((g) => g.taxGroups[tg])
                                                .reduce(
                                                    (acc, g) => acc + g.taxGroups[tg],
                                                    0
                                                );
                                            return (
                                                <td
                                                    key={`total-tg-${idx}`}
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {total > 0 ? formatBRL(total) : ""}
                                                </td>
                                            );
                                        })}

                                        {availablePaymentTypes.map((pt, idx) => {
                                            const total = groupedPaymentsPrestacaoContas
                                                .filter((g) => g.paymentTypes[pt])
                                                .reduce(
                                                    (acc, g) => acc + g.paymentTypes[pt],
                                                    0
                                                );
                                            return (
                                                <td
                                                    key={`total-pt-${idx}`}
                                                    style={{
                                                        textAlign: "right",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {total > 0 ? formatBRL(total) : ""}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </Table>

                            <Table
                                bordered
                                size="sm"
                                style={{
                                    marginTop: "1rem",
                                    border: "2px solid black",
                                    borderCollapse: "collapse",
                                    width: "30%",
                                }}
                            >
                                <tbody>
                                    <tr>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Total Geral:
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {formatBRL(
                                                groupedPaymentsPrestacaoContas.reduce(
                                                    (acc, g) => acc + g.totalAmount,
                                                    0
                                                )
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Total Geral em Depósito:
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {formatBRL(totalDepositPrestacaoContas)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Total Geral em Espécie:
                                        </td>
                                        <td
                                            style={{
                                                textAlign: "right",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {formatBRL(totalCashPrestacaoContas)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            <Button
                                variant="success"
                                onClick={handleGeneratePdfPrestacaoContas}
                                style={{ marginTop: "1rem" }}
                            >
                                Gerar PDF
                            </Button>
                        </>
                    )}
                </Tab>
            </Tabs>
        </div>
    );
};

export default ReportPayments;